/**
 * Icon Library - Loads and caches SVG icons from /content/icons/game-icons/
 *
 * Provides methods to:
 * - Load icon manifest with categories
 * - Fetch SVG files and extract path data
 * - Cache SVG paths for Konva.Path rendering
 */

class IconLibrary {
  constructor() {
    this.manifest = null;
    this.svgCache = new Map(); // iconName -> { path: string, viewBox: string }
    this.loadingPromises = new Map(); // Track in-flight requests
  }

  /**
   * Load the icon manifest
   * @returns {Promise<object>} The manifest data
   */
  async loadManifest() {
    if (this.manifest) {
      return this.manifest;
    }

    try {
      const response = await fetch('/content/icons/manifest.json');
      if (!response.ok) {
        throw new Error(`Failed to load manifest: ${response.status}`);
      }
      this.manifest = await response.json();
      return this.manifest;
    } catch (error) {
      console.error('Error loading icon manifest:', error);
      throw error;
    }
  }

  /**
   * Get categories from manifest
   * @returns {Promise<object>} Categories object
   */
  async getCategories() {
    const manifest = await this.loadManifest();
    return manifest.categories || {};
  }

  /**
   * Load an SVG file and extract path data
   * @param {string} iconName - Icon filename without .svg extension
   * @returns {Promise<object>} { path: string, viewBox: string }
   */
  async loadSvg(iconName) {
    // Check cache first
    if (this.svgCache.has(iconName)) {
      return this.svgCache.get(iconName);
    }

    // Check if already loading
    if (this.loadingPromises.has(iconName)) {
      return this.loadingPromises.get(iconName);
    }

    // Start loading
    const loadPromise = this._fetchAndParseSvg(iconName);
    this.loadingPromises.set(iconName, loadPromise);

    try {
      const result = await loadPromise;
      this.svgCache.set(iconName, result);
      return result;
    } finally {
      this.loadingPromises.delete(iconName);
    }
  }

  /**
   * Internal method to fetch and parse SVG
   * @private
   */
  async _fetchAndParseSvg(iconName) {
    try {
      const response = await fetch(`/content/icons/game-icons/${iconName}.svg`);
      if (!response.ok) {
        throw new Error(`Failed to load SVG: ${response.status}`);
      }

      const svgText = await response.text();

      // Parse SVG to extract path data and viewBox
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, 'image/svg+xml');
      const svgElement = doc.querySelector('svg');

      if (!svgElement) {
        throw new Error('Invalid SVG: no <svg> element found');
      }

      // Get viewBox (default to "0 0 512 512" if not specified)
      const viewBox = svgElement.getAttribute('viewBox') || '0 0 512 512';

      // Extract all path elements and combine their d attributes
      const pathElements = svgElement.querySelectorAll('path');
      const paths = Array.from(pathElements).map(p => p.getAttribute('d')).filter(Boolean);

      if (paths.length === 0) {
        console.warn(`No path data found in SVG: ${iconName}`);
        return { path: '', viewBox };
      }

      // If multiple paths, we'll use the first one (most icons have a single path)
      // For complex icons with multiple paths, we may need to combine them
      const pathData = paths.join(' ');

      return { path: pathData, viewBox };
    } catch (error) {
      console.error(`Error loading SVG ${iconName}:`, error);
      // Return a fallback circle path
      return {
        path: 'M256 256m-200 0a200 200 0 1 0 400 0a200 200 0 1 0 -400 0',
        viewBox: '0 0 512 512'
      };
    }
  }

  /**
   * Get icon path data for Konva.Path
   * @param {string} iconName - Icon filename without .svg extension
   * @returns {Promise<string>} SVG path data string
   */
  async getIconPath(iconName) {
    const { path } = await this.loadSvg(iconName);
    return path;
  }

  /**
   * Get icon viewBox for scaling
   * @param {string} iconName - Icon filename without .svg extension
   * @returns {Promise<string>} ViewBox string (e.g., "0 0 512 512")
   */
  async getIconViewBox(iconName) {
    const { viewBox } = await this.loadSvg(iconName);
    return viewBox;
  }

  /**
   * Preload multiple SVGs in parallel
   * @param {string[]} iconNames - Array of icon names to preload
   * @returns {Promise<void>}
   */
  async preloadSvgs(iconNames) {
    const promises = iconNames.map(name => this.loadSvg(name));
    await Promise.all(promises);
  }

  /**
   * Clear the cache
   */
  clearCache() {
    this.svgCache.clear();
    this.loadingPromises.clear();
  }

  /**
   * Get cached icon count
   * @returns {number}
   */
  getCacheSize() {
    return this.svgCache.size;
  }
}

// Export singleton instance
export const iconLibrary = new IconLibrary();
